import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { History, AlertTriangle, CheckCircle2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import nurseryService from '../../services/nursery';
import legacyImportService, { isLegacyImportOpen } from '../../services/legacyImport';
import { errorMessage } from '../../utils/apiResponse';
import type {
  LegacyAddress, LegacyChoice, LegacyContact, LegacyImportPreview, LegacyImportResult, LegacyLine,
} from '../../types/legacyImport';

/**
 * TEMPORARY (Rob, 2026-10-14): bring a customer across from the legacy system
 * by its number, or fill an existing customer from it (?customer=<id>).
 * Switched off on LEGACY_IMPORT_ENDS; delete this page, its route, the
 * dashboard card and the customer page's Sync button then.
 */

const cardCls = 'bg-white border border-neutral-200 rounded-xl p-4 sm:p-6';
const errorCls = 'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words';
const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const FILL_LABELS: Record<string, string> = {
  contact_name: 'Contact', email: 'Email', phone: 'Phone', abn: 'ABN',
  customer_number: 'Legacy customer number', notes: 'Notes',
};
const HOW: Record<string, string> = {
  chosen: 'chosen here', remembered: 'matched before', exact: 'same cultivar, rootstock and tree type',
};

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'grey' | 'amber' }) {
  const cls = tone === 'green' ? 'bg-green-50 text-green-700 border-green-200'
    : tone === 'amber' ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-neutral-50 text-neutral-500 border-neutral-200';
  return <span className={`inline-block shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

const contactText = (c: LegacyContact) => [c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'No name';
const addressText = (a: LegacyAddress) => [a.line1, a.line2, a.suburb, a.state, a.postcode].filter(Boolean).join(', ');

const NEEDS_MATCH = 'Not matched to a stock item or product';

function LineRow({ line, chosen, choices, onChoose }: {
  line: LegacyLine;
  chosen: string | undefined;
  choices: LegacyChoice[];
  onChoose: (key: string | null) => void;
}) {
  const needsMatch = line.skip_reason === NEEDS_MATCH;
  const suggested = new Set(line.suggestions.map((s) => s.key));
  const products = choices.filter((c) => c.kind === 'product' && !suggested.has(c.key));
  const items = choices.filter((c) => c.kind === 'stock_item' && !suggested.has(c.key));
  return (
    <li className="py-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-800 break-words">{line.description || `Legacy item ${line.item_no}`}</p>
          <p className="text-xs text-neutral-500">
            {line.quantity} trees
            {line.confirmed != null && line.ordered != null && line.confirmed !== line.ordered ? ` (ordered ${line.ordered}, confirmed ${line.confirmed})` : ''}
            {line.order_date ? ` · ordered ${line.order_date}` : ''}
          </p>
          {line.comments && <p className="text-xs text-neutral-500 break-words">{line.comments}</p>}
        </div>
        {line.already_imported ? <Badge tone="grey">Already imported</Badge>
          : line.skip_reason && !needsMatch ? <Badge tone="grey">{line.skip_reason}</Badge>
            : needsMatch && !chosen ? <Badge tone="amber">Choose a product or stock item</Badge>
              : <Badge tone="green">Will import</Badge>}
      </div>
      {line.match && !line.already_imported && (
        <p className="text-xs text-neutral-600 break-words">
          As {line.match.kind === 'product' ? 'product ' : ''}<span className="font-medium">{line.match.display_name}</span> ({HOW[line.match.how]})
        </p>
      )}
      {needsMatch && (
        <select
          aria-label={`Product or stock item for ${line.description}`}
          className={inputCls}
          value={chosen ?? ''}
          onChange={(e) => onChoose(e.target.value || null)}
        >
          <option value="">Leave it out</option>
          {line.suggestions.length > 0 && (
            <optgroup label="Same cultivar and rootstock">
              {line.suggestions.map((s) => (
                <option key={s.key} value={s.key}>{s.kind === 'product' ? `Product: ${s.display_name}` : s.display_name}</option>
              ))}
            </optgroup>
          )}
          {products.length > 0 && (
            <optgroup label="Products">
              {products.map((c) => <option key={c.key} value={c.key}>{c.display_name}</option>)}
            </optgroup>
          )}
          <optgroup label="Stock items">
            {items.map((c) => <option key={c.key} value={c.key}>{c.display_name}</option>)}
          </optgroup>
        </select>
      )}
    </li>
  );
}

function LegacyCustomerImport() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const syncId = /^\d+$/.test(params.get('customer') || '') ? Number(params.get('customer')) : null;

  const accounts = useModuleLicense('accounts', ['user', 'admin']);
  const stock = useStockSeat('admin');
  const seatLoading = accounts.loading || stock.loading;
  const hasAccess = accounts.hasAccess || stock.hasAccess;

  const [number, setNumber] = useState(params.get('number') || '');
  const [syncName, setSyncName] = useState<string | null>(null);
  const [preview, setPreview] = useState<LegacyImportPreview | null>(null);
  const [matches, setMatches] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LegacyImportResult | null>(null);

  // Syncing: the customer it fills, and its legacy number when it already has one.
  useEffect(() => {
    if (!syncId || !hasAccess) return;
    let cancelled = false;
    nurseryService.getCustomer(syncId).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      setSyncName(res.data.name);
      if (res.data.customer_number) setNumber((n) => n || res.data!.customer_number!);
    }).catch(() => { /* the preview says what is wrong */ });
    return () => { cancelled = true; };
  }, [syncId, hasAccess]);

  const lookUp = async () => {
    const n = number.trim();
    if (!/^\d+$/.test(n)) { setError('A legacy customer number is digits only.'); return; }
    setBusy(true); setError(null); setPreview(null); setResult(null); setMatches({});
    try {
      const res = await legacyImportService.preview(n, syncId);
      if (res.success && res.data) setPreview(res.data);
      else setError(errorMessage(res, 'Could not read that customer from the legacy system'));
    } catch (e) {
      setError(errorMessage(e, 'Could not read that customer from the legacy system'));
    } finally {
      setBusy(false);
    }
  };

  const lines = useMemo(() => preview?.orders.flatMap((o) => o.lines) ?? [], [preview]);
  const willImport = lines.filter((l) => !l.skip_reason || (l.skip_reason === NEEDS_MATCH && l.item_no != null && matches[l.item_no]));
  const unmatched = lines.filter((l) => l.skip_reason === NEEDS_MATCH && !(l.item_no != null && matches[l.item_no]));

  const runImport = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const res = await legacyImportService.import(String(preview.cust_no), matches, syncId);
      if (res.success && res.data) { setResult(res.data); setPreview(null); }
      else setError(errorMessage(res, 'The import did not finish'));
    } catch (e) {
      setError(errorMessage(e, 'The import did not finish'));
    } finally {
      setBusy(false);
    }
  };

  const target = preview?.existing_customer;
  const header = (
    <PageHeader
      title={syncId ? 'Sync from the legacy system' : 'Import a legacy customer'}
      subtitle={syncId ? (syncName ? `Fill ${syncName} from hh.factree` : 'Fill a customer from hh.factree') : 'Customer, contacts, addresses and orders not invoiced, from hh.factree'}
      icon={<History className="h-6 w-6 text-white" />}
      actionButtons={(
        <Button variant="outline" size="sm" onClick={() => navigate(syncId ? `/accounts/customers/${syncId}` : '/nursery')}>
          Back
        </Button>
      )}
    />
  );

  if (seatLoading) return <>{header}<div className="max-w-4xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div></>;
  if (!hasAccess) {
    return (
      <>{header}
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className={cardCls}><p className="text-sm text-neutral-700">Importing customers needs a Nursery Admin seat or an Accounts seat.</p></div>
        </div>
      </>
    );
  }
  if (!isLegacyImportOpen()) {
    return (
      <>{header}
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className={cardCls}><p className="text-sm text-neutral-700">Importing customers from the legacy system has been switched off.</p></div>
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className={cardCls}>
          <form
            className="flex flex-col sm:flex-row gap-2 sm:items-end"
            onSubmit={(e) => { e.preventDefault(); lookUp(); }}
          >
            <label className="flex-1 min-w-0 text-sm font-medium text-neutral-700">
              Legacy customer number
              <input
                className={`${inputCls} mt-1`}
                inputMode="numeric"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="e.g. 10423"
              />
            </label>
            <Button type="submit" disabled={busy || !number.trim()}>{busy && !preview ? 'Reading…' : 'Look up'}</Button>
          </form>
          <p className="text-xs text-neutral-500 mt-3">
            Nothing is saved until you press Import. Orders come across as drafts, priced from the customer&rsquo;s price list.
            This page is temporary and stops working on 28 October 2026.
          </p>
        </div>

        {error && <div className={errorCls}>{error}</div>}

        {result && (
          <div className={`${cardCls} space-y-3`}>
            <p className="flex items-start gap-2 text-sm text-neutral-800">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
              <span className="min-w-0 break-words">
                {result.created ? 'Created ' : 'Updated '}
                <Link className="text-primary-600 hover:underline font-medium" to={`/accounts/customers/${result.customer_id}`}>{result.customer_name}</Link>
                {`: ${result.contacts_added} contact${result.contacts_added === 1 ? '' : 's'} and ${result.addresses_added} address${result.addresses_added === 1 ? '' : 'es'} added`}
                {result.fields_filled.length > 0 ? `, ${result.fields_filled.map((f) => FILL_LABELS[f] ?? f).join(', ').toLowerCase()} filled in` : ''}.
              </span>
            </p>
            {result.orders.length > 0 ? (
              <ul className="text-sm space-y-1">
                {result.orders.map((o) => (
                  <li key={o.id} className="break-words">
                    <Link className="text-primary-600 hover:underline" to={`/nursery/orders/${o.id}`}>{o.order_number}</Link>
                    {` · draft · ${o.trees} trees on ${o.lines} line${o.lines === 1 ? '' : 's'}`}
                    {o.year_of_sale ? ` · season ${o.year_of_sale}` : ''}
                    {o.purchase_order_no ? ` · PO ${o.purchase_order_no}` : ''}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-neutral-600">No orders were brought across.</p>}
            {result.already_imported > 0 && (
              <p className="text-sm text-neutral-600">{result.already_imported} line{result.already_imported === 1 ? ' was' : 's were'} already imported and left alone.</p>
            )}
            {result.skipped_lines.length > 0 && (
              <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="font-medium">Left out, to enter by hand:</p>
                <ul className="list-disc pl-5">
                  {result.skipped_lines.map((l, i) => (
                    <li key={i} className="break-words">{`${l.quantity} × ${l.description}${l.year_of_sale ? ` (season ${l.year_of_sale})` : ''}: ${l.reason}`}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {preview && (
          <>
            <div className={`${cardCls} space-y-2`}>
              <h2 className="text-base font-semibold text-neutral-800 break-words">
                {preview.customer.name} <span className="text-neutral-500 font-normal">· legacy {preview.cust_no}</span>
              </h2>
              <p className="text-sm text-neutral-700 break-words">
                {target
                  ? <>Goes onto <Link className="text-primary-600 hover:underline" to={`/accounts/customers/${target.id}`}>{target.name}</Link>, which keeps everything it has: only blanks are filled, and contacts and addresses it does not have are added.</>
                  : 'A new customer will be created.'}
              </p>
              {preview.warnings.map((w) => (
                <p key={w} className="flex items-start gap-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" /><span className="min-w-0 break-words">{w}</span>
                </p>
              ))}
              {target ? (
                Object.keys(preview.fills).length > 0 ? (
                  <dl className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {Object.entries(preview.fills).filter(([k]) => k !== 'notes').map(([k, v]) => (
                      <div key={k} className="min-w-0"><dt className="text-neutral-500">{FILL_LABELS[k] ?? k}</dt><dd className="break-words">{v}</dd></div>
                    ))}
                    {preview.fills.notes && <div className="min-w-0"><dt className="text-neutral-500">Notes</dt><dd>Filled from the legacy record</dd></div>}
                  </dl>
                ) : <p className="text-sm text-neutral-500">No blank fields to fill.</p>
              ) : (
                <dl className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  <div className="min-w-0"><dt className="text-neutral-500">Contact</dt><dd className="break-words">{preview.customer.contact_name || 'None'}</dd></div>
                  <div className="min-w-0"><dt className="text-neutral-500">Email</dt><dd className="break-words">{preview.customer.email || 'None'}</dd></div>
                  <div className="min-w-0"><dt className="text-neutral-500">Phone</dt><dd className="break-words">{preview.customer.phone || 'None'}</dd></div>
                  <div className="min-w-0"><dt className="text-neutral-500">ABN</dt><dd className="break-words">{preview.customer.abn || 'None'}</dd></div>
                </dl>
              )}
            </div>

            <div className={cardCls}>
              <h2 className="text-base font-semibold text-neutral-800 mb-2">Contacts</h2>
              {preview.contacts.length === 0 ? <p className="text-sm text-neutral-500">None in the legacy system.</p> : (
                <ul className="divide-y divide-neutral-100">
                  {preview.contacts.map((c, i) => (
                    <li key={i} className="py-2 flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 text-sm">
                        <p className="font-medium text-neutral-800 break-words">{contactText(c)}{c.is_main ? ' (main)' : ''}{c.position ? ` · ${c.position}` : ''}</p>
                        <p className="text-neutral-500 break-words">{[c.email, c.phone, c.mobile].filter(Boolean).join(' · ') || 'No details'}</p>
                      </div>
                      {c.will_add ? <Badge tone="green">Will add</Badge> : <Badge tone="grey">Already there</Badge>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={cardCls}>
              <h2 className="text-base font-semibold text-neutral-800 mb-2">Addresses</h2>
              {preview.addresses.length === 0 ? <p className="text-sm text-neutral-500">No delivery or billing address in the legacy system.</p> : (
                <ul className="divide-y divide-neutral-100">
                  {preview.addresses.map((a, i) => (
                    <li key={i} className="py-2 flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 text-sm">
                        <p className="font-medium text-neutral-800">
                          {a.address_type === 'billing' ? 'Billing' : `Delivery${a.is_default ? ' (default)' : ''}`}{a.label ? ` · ${a.label}` : ''}
                        </p>
                        <p className="text-neutral-600 break-words">{addressText(a)}</p>
                      </div>
                      {a.will_add ? <Badge tone="green">Will add</Badge> : <Badge tone="grey">{a.address_type === 'billing' ? 'Has one' : 'Already there'}</Badge>}
                    </li>
                  ))}
                </ul>
              )}
              {preview.skipped_addresses.length > 0 && (
                <div className="mt-3 text-xs text-neutral-500 space-y-1">
                  {preview.skipped_addresses.map((a, i) => <p key={i} className="break-words">{`Not kept: ${a.text} (${a.legacy_type}). ${a.reason}.`}</p>)}
                </div>
              )}
            </div>

            <div className={cardCls}>
              <h2 className="text-base font-semibold text-neutral-800">Orders not invoiced</h2>
              <p className="text-sm text-neutral-500 mb-2">One draft order per season and purchase order.</p>
              {preview.orders.length === 0 ? <p className="text-sm text-neutral-500">None in the legacy system.</p> : preview.orders.map((g) => (
                <div key={g.key} className="mt-3">
                  <h3 className="text-sm font-semibold text-neutral-700 break-words">
                    {g.year_of_sale ? `Season ${g.year_of_sale}` : 'No season'}{g.purchase_order_no ? ` · PO ${g.purchase_order_no}` : ''}
                  </h3>
                  <ul className="divide-y divide-neutral-100">
                    {g.lines.map((l) => (
                      <LineRow
                        key={l.ref}
                        line={l}
                        chosen={l.item_no != null ? matches[l.item_no] : undefined}
                        choices={preview.choices}
                        onChoose={(id) => setMatches((m) => {
                          if (l.item_no == null) return m;
                          const next = { ...m };
                          if (id) next[l.item_no] = id; else delete next[l.item_no];
                          return next;
                        })}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className={`${cardCls} flex flex-col sm:flex-row sm:items-center justify-between gap-3`}>
              <p className="text-sm text-neutral-700">
                {willImport.length} line{willImport.length === 1 ? '' : 's'} to bring across
                {unmatched.length > 0 ? `, ${unmatched.length} left out until a product or stock item is chosen` : ''}.
                {' '}A match chosen here is remembered for the next customer.
              </p>
              <Button onClick={runImport} disabled={busy}>
                {busy ? 'Importing…' : target ? 'Sync customer' : 'Import customer'}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default LegacyCustomerImport;
